import { useEffect, useRef, useState } from 'react';
import { NumberedPager } from '@/components/lists';
import { getAccountsSync, getCoverageSync, getUploadsSync } from '../store';
import { longDate, shortDate, spanLabel } from '../dates';
import { money } from '../utils';

const PAGE = 20;

/**
 * The one-line coverage note on Matched and Bank deposits (rcm-32 §4, §7):
 * "Statements cover Jul 1 – Oct 14 · Chase ··4417 · See uploads", plus any days
 * with no statement. "See uploads" opens the list of every upload.
 */
export function CoverageLine({ className = 'bm-cov' }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const cov = getCoverageSync();
  if (!cov) {
    return <div className={className}>No bank statement yet. Use Upload bank statement at the top right.</div>;
  }
  const names = cov.accounts.map((a) => a.short).join(', ');
  return (
    <div className={className}>
      <div>
        Statements cover {shortDate(cov.from)} – {shortDate(cov.to)} · {names} ·{' '}
        <button type="button" className="bm-link-btn" onClick={() => { setOpen(true); }}>See uploads</button>
      </div>
      {cov.holes.length > 0 ? (
        <div className="bm-hole">No statement for {cov.holes.map((h) => spanLabel(h.from, h.to)).join(', ')}</div>
      ) : null}
      {open ? <UploadsDialog onClose={() => { setOpen(false); }} /> : null}
    </div>
  );
}

/** Every upload, newest first: file, account, dates covered, lines read, deposits kept, who and when. 20 a page. */
export function UploadsDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const uploads = getUploadsSync();
  const accounts = getAccountsSync();
  const rows = uploads.slice((page - 1) * PAGE, page * PAGE);

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus();
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return (
    <div className="po-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div ref={ref} className="po-dialog bm-dialog" role="dialog" aria-modal="true" aria-label="Bank statement uploads">
        <div className="bm-dialog-head">
          <div>
            <h2 className="po-dialog-title">Bank statement uploads</h2>
            <p className="po-dialog-body">Newest first. Each file is read once; lines already uploaded are skipped.</p>
          </div>
          <button type="button" className="po-btn po-btn-sm" onClick={onClose}>Close</button>
        </div>
        <div className="po-tbl-wrap">
          <table className="po-tbl bm-uptbl">
            <thead>
              <tr>
                <th>File</th>
                <th>Account</th>
                <th>Dates covered</th>
                <th className="ta-right">Lines read</th>
                <th className="ta-right">Deposits kept</th>
                <th>Uploaded by</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id}>
                  <td className="bm-mono">{u.fileName}</td>
                  <td>{accounts.find((a) => a.id === u.accountId)?.short ?? u.accountId}</td>
                  <td>{spanLabel(u.fromIso, u.toIso)}, {longDate(u.toIso).slice(-4)}</td>
                  <td className="ta-right num">{u.linesRead.toLocaleString()}</td>
                  <td className="ta-right num">
                    {u.kept} · {money(u.keptTotal)}
                    <span className="bm-sub">{u.matched} matched, {u.dontMatch} {"don't match"}</span>
                  </td>
                  <td>{u.uploadedBy}</td>
                  <td>{u.uploadedLabel}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <NumberedPager page={page} pageSize={PAGE} total={uploads.length} onPage={setPage} />
      </div>
    </div>
  );
}
